import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, Edit, Trash2, CheckCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';

const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    enquiry_date: new Date().toISOString().split('T')[0],
    student_name: '',
    phone: '',
    email: '',
    course_interested: '',
    source: 'Walk-in',
    parent_name: '',
    address: '',
    follow_up_date: '',
    notes: '',
    status: 'New',
    assigned_to: ''
  });

  useEffect(() => { fetchEnquiries(); }, []);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/frontdesk/enquiries');
      setEnquiries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/frontdesk/enquiries', formData);
      alert('Enquiry added successfully!');
      setShowModal(false);
      resetForm();
      fetchEnquiries();
    } catch (error) {
      alert('Failed to add enquiry');
    }
  };

  const handleStatusChange = async (enquiryId, status) => {
    try {
      await apiClient.put(`/api/frontdesk/enquiries/${enquiryId}/status?status=${status}`);
      fetchEnquiries();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      await apiClient.delete(`/api/frontdesk/enquiries/${enquiryId}`);
      fetchEnquiries();
    } catch (error) {
      alert('Failed to delete enquiry');
    }
  };

  const resetForm = () => {
    setFormData({
      enquiry_date: new Date().toISOString().split('T')[0],
      student_name: '',
      phone: '',
      email: '',
      course_interested: '',
      source: 'Walk-in',
      parent_name: '',
      address: '',
      follow_up_date: '',
      notes: '',
      status: 'New',
      assigned_to: ''
    });
  };

  const filteredEnquiries = enquiries.filter(e =>
    e.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.phone?.includes(searchTerm) ||
    e.course_interested?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Enquiry List</CardTitle>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Enquiry
          </Button>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by name, phone, or course..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Enquiry #</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEnquiries.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      {loading ? 'Loading...' : 'No enquiries found. Click "Add Enquiry" to create a new entry.'}
                    </td>
                  </tr>
                ) : (
                  filteredEnquiries.map((enquiry) => (
                    <tr key={enquiry.id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm font-medium">{enquiry.enquiry_number || '-'}</td>
                      <td className="p-3 text-sm">{enquiry.enquiry_date}</td>
                      <td className="p-3 text-sm font-medium">{enquiry.student_name}</td>
                      <td className="p-3 text-sm">{enquiry.phone}</td>
                      <td className="p-3 text-sm">{enquiry.course_interested}</td>
                      <td className="p-3 text-sm">{enquiry.source}</td>
                      <td className="p-3">
                        <Select value={enquiry.status} onValueChange={(v) => handleStatusChange(enquiry.id, v)}>
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="New">New</SelectItem>
                            <SelectItem value="Follow-up">Follow-up</SelectItem>
                            <SelectItem value="Interested">Interested</SelectItem>
                            <SelectItem value="Converted">Converted</SelectItem>
                            <SelectItem value="Closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleDelete(enquiry.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Enquiry Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add New Enquiry</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Enquiry Date *</Label>
                  <Input type="date" required value={formData.enquiry_date} onChange={(e) => setFormData({...formData, enquiry_date: e.target.value})} />
                </div>
                <div>
                  <Label>Source *</Label>
                  <Select value={formData.source} onValueChange={(v) => setFormData({...formData, source: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Walk-in">Walk-in</SelectItem>
                      <SelectItem value="Phone">Phone</SelectItem>
                      <SelectItem value="Website">Website</SelectItem>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Social Media">Social Media</SelectItem>
                      <SelectItem value="Advertisement">Advertisement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Student Name *</Label>
                  <Input required value={formData.student_name} onChange={(e) => setFormData({...formData, student_name: e.target.value})} />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                </div>
                <div>
                  <Label>Course Interested *</Label>
                  <Input required value={formData.course_interested} onChange={(e) => setFormData({...formData, course_interested: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Parent Name</Label>
                  <Input value={formData.parent_name} onChange={(e) => setFormData({...formData, parent_name: e.target.value})} />
                </div>
                <div>
                  <Label>Follow-up Date</Label>
                  <Input type="date" value={formData.follow_up_date} onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit">Add Enquiry</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryList;
